import { useEffect, useRef, useState, useCallback } from 'react';

declare global {
  interface Window {
    google?: {
      ima?: {
        AdDisplayContainer: new (container: HTMLElement, videoElement: HTMLVideoElement) => any;
        AdsLoader: new (container: any) => any;
        AdsRequest: new () => any;
        AdsRenderingSettings: new () => any;
        AdsManagerLoadedEvent: {
          Type: {
            ADS_MANAGER_LOADED: string;
          };
        };
        AdEvent: {
          Type: {
            LOADED: string;
            STARTED: string;
            COMPLETE: string;
            SKIPPED: string;
          };
        };
        AdErrorEvent: {
          Type: {
            AD_ERROR: string;
          };
        };
        ViewMode: {
          NORMAL: string;
        };
      };
    };
  }
}

interface UseVideoAdOptions {
  adTagUrl: string;
  onAdLoaded?: () => void;
  onAdStarted?: () => void;
  onAdComplete?: () => void;
  onAdError?: (error: any) => void;
  onAdSkipped?: () => void;
  timeout?: number; // Timeout in milliseconds
}

export function useVideoAd({
  adTagUrl,
  onAdLoaded,
  onAdStarted,
  onAdComplete,
  onAdError,
  onAdSkipped,
  timeout = 10000, // 10 second default timeout
}: UseVideoAdOptions) {
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const adDisplayContainerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const adsManagerRef = useRef<any>(null);
  const adsLoaderRef = useRef<any>(null);
  const adDisplayContainerInstanceRef = useRef<any>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoadingRef = useRef(false); // Guard to prevent multiple simultaneous loads
  const isPlayingRef = useRef(false); // Track if ad is playing (for timeout check)

  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (adsManagerRef.current) {
      try {
        adsManagerRef.current.destroy();
      } catch (e) {
        console.warn('[VideoAd] Error destroying ads manager:', e);
      }
      adsManagerRef.current = null;
    }
    if (adsLoaderRef.current) {
      try {
        adsLoaderRef.current.contentComplete();
      } catch (e) {
        console.warn('[VideoAd] Error completing ads loader:', e);
      }
      adsLoaderRef.current = null;
    }
    // Clean up the ad display container instance
    if (adDisplayContainerInstanceRef.current) {
      try {
        // The AdDisplayContainer doesn't have a destroy method, but we should clear the ref
        // Reset the container element to ensure clean state for next load
        if (adDisplayContainerRef.current) {
          // Clear any child elements that might have been added by IMA SDK
          const container = adDisplayContainerRef.current;
          // Don't clear innerHTML as it might contain the video element
          // Just clear the ref so a new container can be created
        }
      } catch (e) {
        console.warn('[VideoAd] Error cleaning up ad display container:', e);
      }
      adDisplayContainerInstanceRef.current = null;
    }
    // Reset loading guard so ads can load again
    isLoadingRef.current = false;
    isPlayingRef.current = false;
    setIsLoading(false);
    setIsPlaying(false);
  }, []);

  // Wait for IMA SDK to load
  const waitForIMASDK = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.google?.ima) {
        resolve();
        return;
      }

      // Check if script is already in the DOM
      const existingScript = document.querySelector('script[src*="ima3.js"]');
      if (!existingScript) {
        // Dynamically load the SDK if not present
        console.log('[VideoAd] Loading IMA SDK dynamically');
        const script = document.createElement('script');
        script.src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
        script.async = true;
        script.onload = () => {
          // Wait for the SDK to initialize (may take a moment)
          let attempts = 0;
          const maxAttempts = 30; // 3 seconds
          const checkInterval = setInterval(() => {
            attempts++;
            if (window.google?.ima) {
              clearInterval(checkInterval);
              console.log('[VideoAd] IMA SDK loaded and initialized');
              resolve();
            } else if (attempts >= maxAttempts) {
              clearInterval(checkInterval);
              reject(new Error('IMA SDK script loaded but not initialized'));
            }
          }, 100);
        };
        script.onerror = () => {
          reject(new Error('Failed to load IMA SDK script'));
        };
        document.head.appendChild(script);
        return;
      }

      // Script exists, wait for it to load (max 10 seconds)
      // The script might be loading with defer, so give it more time
      let attempts = 0;
      const maxAttempts = 100; // 100 * 100ms = 10 seconds
      
      const checkInterval = setInterval(() => {
        attempts++;
        if (window.google?.ima) {
          clearInterval(checkInterval);
          console.log('[VideoAd] IMA SDK detected after', attempts * 100, 'ms');
          resolve();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          console.error('[VideoAd] IMA SDK timeout - window.google:', window.google);
          reject(new Error('IMA SDK failed to load within timeout'));
        }
      }, 100);
    });
  }, []);

  const loadAd = useCallback(async () => {
    // Only cleanup if there's actually something to clean up
    if (adsManagerRef.current || adsLoaderRef.current || timeoutRef.current || adDisplayContainerInstanceRef.current) {
      cleanup();
      // Increased delay for Brave - it may need more time to fully clean up resources
      // This helps prevent race conditions when creating a new AdDisplayContainer
      await new Promise(resolve => setTimeout(resolve, 150));
    }
    
    if (!adDisplayContainerRef.current || !videoRef.current) {
      console.error('[VideoAd] Ad display container or video element not available');
      setError('Ad container not available');
      onAdError?.(new Error('Ad container not available'));
      return;
    }

    // Guard against multiple simultaneous loads
    if (isLoadingRef.current) {
      console.log('[VideoAd] Ad is already loading, skipping duplicate call');
      return;
    }

    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);

    // Wait for IMA SDK to be available
    try {
      await waitForIMASDK();
      console.log('[VideoAd] IMA SDK loaded successfully');
    } catch (err: any) {
      console.error('[VideoAd] IMA SDK not loaded:', err);
      setIsLoading(false);
      setError('Ad SDK not loaded');
      onAdError?.(err);
      return;
    }

    try {
      // Create ad display container with video element
      const adDisplayContainer = new window.google.ima.AdDisplayContainer(
        adDisplayContainerRef.current,
        videoRef.current
      );
      adDisplayContainerInstanceRef.current = adDisplayContainer;

      // Initialize the ad display container
      adDisplayContainer.initialize();

      // Create ads loader
      const adsLoader = new window.google.ima.AdsLoader(adDisplayContainer);
      adsLoaderRef.current = adsLoader;

      // Set up event listeners
      adsLoader.addEventListener(
        window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
        (adsManagerLoadedEvent: any) => {
          console.log('[VideoAd] ADS_MANAGER_LOADED event fired');
          // Clear timeout since ad manager loaded (request was successful)
          if (timeoutRef.current) {
            console.log('[VideoAd] Clearing timeout - ad manager loaded');
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          
          // Get the ads manager with rendering settings
          const adsRenderingSettings = new window.google.ima.AdsRenderingSettings();
          let adsManager;
          try {
            adsManager = adsManagerLoadedEvent.getAdsManager(
              videoRef.current,
              adsRenderingSettings
            );
            console.log('[VideoAd] Got ads manager from event');
          } catch (e: any) {
            console.error('[VideoAd] Error getting ads manager:', e);
            isLoadingRef.current = false;
            setIsLoading(false);
            setError('Failed to get ads manager');
            onAdError?.(e);
            cleanup();
            return;
          }
          
          if (!adsManager) {
            console.error('[VideoAd] Ads manager is null');
            isLoadingRef.current = false;
            setIsLoading(false);
            setError('Ads manager is null');
            onAdError?.(new Error('Ads manager is null'));
            cleanup();
            return;
          }
          
          adsManagerRef.current = adsManager;
          console.log('[VideoAd] Ads manager stored, setting up event listeners...');

          // Set up ads manager event listeners
          adsManager.addEventListener(
            window.google.ima.AdEvent.Type.LOADED,
            () => {
              console.log('[VideoAd] Ad loaded');
              // Clear timeout since ad loaded (even if it hasn't started yet)
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
              setIsLoading(false);
              onAdLoaded?.();
              
              // Don't set a timeout here - let the ad try to play
              // The ad will either start playing (STARTED event) or error (AD_ERROR event)
              // We shouldn't destroy the ad just because it takes time to start
            }
          );

          adsManager.addEventListener(
            window.google.ima.AdEvent.Type.STARTED,
            () => {
              console.log('[VideoAd] Ad started');
              // Clear the playback timeout since ad started
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
              isPlayingRef.current = true;
              setIsPlaying(true);
              onAdStarted?.();
            }
          );

          adsManager.addEventListener(
            window.google.ima.AdEvent.Type.COMPLETE,
            () => {
              console.log('[VideoAd] Ad completed');
              isPlayingRef.current = false;
              setIsPlaying(false);
              isLoadingRef.current = false;
              onAdComplete?.();
              cleanup();
            }
          );

          adsManager.addEventListener(
            window.google.ima.AdEvent.Type.SKIPPED,
            () => {
              console.log('[VideoAd] Ad skipped');
              isLoadingRef.current = false;
              isPlayingRef.current = false;
              setIsPlaying(false);
              onAdSkipped?.();
              cleanup();
            }
          );

          // Set up error listener - this will catch errors during ad loading/playback
          adsManager.addEventListener(
            window.google.ima.AdErrorEvent.Type.AD_ERROR,
            (adErrorEvent: any) => {
              const error = adErrorEvent.getError();
              const errorCode = error?.getErrorCode?.();
              const errorMessage = error?.getMessage?.() || 'Unknown ad error';
              console.error('[VideoAd] Ad error:', {
                code: errorCode,
                message: errorMessage,
                error: error
              });
              
              // Clear timeout since we got an error
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
              
              isLoadingRef.current = false;
              isPlayingRef.current = false;
              setIsLoading(false);
              setIsPlaying(false);
              setError(errorMessage);
              onAdError?.(error);
              cleanup();
            }
          );

          // Initialize the ads manager
          try {
            console.log('[VideoAd] Initializing ads manager...');
            adsManager.init(640, 360, window.google.ima.ViewMode.NORMAL);
            console.log('[VideoAd] Starting ads manager...');
            adsManager.start();
            console.log('[VideoAd] Ads manager started, waiting for ad to load...');
            
            // Set a timeout in case ad doesn't load after manager starts
            // This handles cases where manager loads but ad request fails silently (e.g., Brave blocking)
            isPlayingRef.current = false; // Reset playing state
            timeoutRef.current = setTimeout(() => {
              // Check if ad loaded (we should have seen LOADED event by now)
              // If not, the ad request might have failed silently
              console.warn('[VideoAd] Ad manager started but ad did not load within 8 seconds');
              // Clear timeout ref
              timeoutRef.current = null;
              isLoadingRef.current = false;
              setIsLoading(false);
              setError('Ad did not load');
              onAdError?.(new Error('Ad did not load'));
              cleanup();
            }, 8000); // 8 second timeout for ad to load after manager starts
          } catch (e: any) {
            console.error('[VideoAd] Error starting ads manager:', e);
            isLoadingRef.current = false;
            setIsLoading(false);
            setError(e.message || 'Failed to start ad');
            onAdError?.(e);
            cleanup();
          }
        },
        false
      );

      adsLoader.addEventListener(
        window.google.ima.AdErrorEvent.Type.AD_ERROR,
        (adErrorEvent: any) => {
          const error = adErrorEvent.getError();
          console.error('[VideoAd] Ads loader error:', error);
          // Clear timeout since we got an error response
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          isLoadingRef.current = false;
          setIsLoading(false);
          setError(error.getMessage());
          onAdError?.(error);
          cleanup();
        },
        false
      );

      // Request ads
      const adsRequest = new window.google.ima.AdsRequest();
      adsRequest.adTagUrl = adTagUrl;
      adsRequest.linearAdSlotWidth = 640;
      adsRequest.linearAdSlotHeight = 360;

      console.log('[VideoAd] Requesting ads with tag:', adTagUrl);
      adsLoader.requestAds(adsRequest);

      // Set timeout - this will fire if ADS_MANAGER_LOADED never fires
      console.log(`[VideoAd] Setting timeout for ${timeout}ms`);
      timeoutRef.current = setTimeout(() => {
        // Check if timeout was already cleared (ad loaded successfully)
        if (!timeoutRef.current) {
          console.log('[VideoAd] Timeout fired but was already cleared (ad loaded)');
          return;
        }
        console.warn('[VideoAd] Ad load timeout - no response from ad server');
        timeoutRef.current = null;
        isLoadingRef.current = false;
        setIsLoading(false);
        setError('Ad load timeout');
        onAdError?.(new Error('Ad load timeout'));
        cleanup();
      }, timeout);
    } catch (e: any) {
      console.error('[VideoAd] Error loading ad:', e);
      isLoadingRef.current = false;
      setIsLoading(false);
      setError(e.message || 'Failed to load ad');
      onAdError?.(e);
      cleanup();
    }
  }, [adTagUrl, onAdLoaded, onAdStarted, onAdComplete, onAdError, onAdSkipped, timeout, cleanup]);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    adDisplayContainerRef,
    videoRef,
    isLoading,
    isPlaying,
    error,
    loadAd,
    cleanup,
  };
}

